import { useCallback, useEffect, useState } from 'react';
import type { UiPromptTemplate } from '@nightshift/ui-types';
import { api } from '../live/api.ts';
import { confirmDiscardChanges } from '../lib/unsaved.ts';
import { announceSettingsSaved, useSettingsAutosave } from '../lib/settings-autosave.tsx';
import { Empty, Spinner } from '../components/primitives.tsx';
import './prompts-settings.css';

export function PromptsPanel({ canWrite }: { canWrite: boolean }) {
  const [templates, setTemplates] = useState<UiPromptTemplate[] | null>(null);
  const [selected, setSelected] = useState('sidecar-dependency-scan.md');
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  async function load() {
    try { setTemplates((await api.prompts()).templates); setError(null); }
    catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
  }
  useEffect(() => { void load(); }, []);
  if (!templates) return error ? <Empty title="Prompts could not be read." body={error} action={<button className="btn" onClick={() => void load()}>Try again</button>} /> : <Spinner />;
  const current = templates.find((row) => row.id === selected) ?? templates[0];
  const visible = templates.filter((row) => `${row.name} ${row.group} ${row.source}`.toLowerCase().includes(filter.toLowerCase()));
  return <div className="prompts-panel">
    <p className="sheet-note">Templates are shared by all projects. Saved overrides apply when the next invocation is assembled; running jobs keep their existing prompt. Ticket details and repository context are added at runtime. Results must still match the job’s required format.</p>
    <div className="prompts-layout">
      <nav className="prompts-list" aria-label="Prompt templates">
        <label htmlFor="prompt-search">Find a prompt</label>
        <input id="prompt-search" className="input" type="search" value={filter} onChange={(event) => setFilter(event.target.value)} />
        {Array.from(new Set(visible.map((row) => row.group))).map((group) => <section key={group}>
          <h3 className="kicker">{group}</h3>
          {visible.filter((row) => row.group === group).map((row) => <button key={row.id} type="button" className={`prompts-item${row.id === current?.id ? ' is-selected' : ''}`} aria-current={row.id === current?.id ? 'true' : undefined} onClick={() => { if (confirmDiscardChanges()) setSelected(row.id); }}>
            <span>{row.name}</span><small>{row.overridden ? 'Custom' : row.editable ? 'Default' : 'Read-only'}</small>
          </button>)}
        </section>)}
        {visible.length === 0 ? <p className="dim">No matching prompts.</p> : null}
      </nav>
      {current ? <PromptEditor key={current.id} template={current} canWrite={canWrite} onSaved={(saved) => setTemplates((rows) => rows!.map((row) => row.id === saved.id ? saved : row))} /> : null}
    </div>
  </div>;
}

function PromptEditor({ template, canWrite, onSaved }: { template: UiPromptTemplate; canWrite: boolean; onSaved: (template: UiPromptTemplate) => void }) {
  const [showDefault, setShowDefault] = useState(false);
  const persist = useCallback(async (text: string) => {
    const result = await api.savePrompt(template.id, text, template.revision);
    onSaved(result.template);
  }, [onSaved, template.id, template.revision]);
  const autosave = useSettingsAutosave({
    value: template.text,
    save: persist,
    what: `${template.name} prompt`,
    enabled: canWrite && template.editable && !showDefault,
    delay: 800,
    validate: (text) => text.trim() ? null : 'Prompt text cannot be empty.',
  });
  const { draft: text, setDraft: setText, status, error } = autosave;
  async function reset() {
    const result = await api.savePrompt(template.id, null, template.revision);
    setShowDefault(false);
    onSaved(result.template);
    announceSettingsSaved('Bundled default restored');
  }
  return <section className="sheet-panel prompt-editor" aria-label={template.name}>
    <header className="panel-head"><h2>{template.name}</h2><span className="chip">{template.overridden ? 'Custom override' : template.editable ? 'Bundled default' : 'Read-only'}</span></header>
    <div className="sheet-panel-body">
      <p>{template.description}</p>
      <p className="dim mono prompt-source">{template.source}</p>
      {template.source === 'Settings → Reviewers' || template.id === 'ai-review.md' ? <a className="link-button" href="?group=reviewers">Edit reviewer prompts</a> : null}
      {template.updatedAt ? <p className="dim">Last saved {new Date(template.updatedAt).toLocaleString()} by {template.updatedBy ?? 'an administrator'}.</p> : null}
      <label htmlFor="prompt-content">{showDefault ? 'Bundled default' : 'Prompt text'}</label>
      <textarea id="prompt-content" className="input mono prompt-content" spellCheck={false} value={showDefault ? template.defaultText : text} readOnly={showDefault || !template.editable || !canWrite} disabled={status === 'saving'} maxLength={40000} onChange={(event) => setText(event.target.value)} />
      {error ? <p className="form-error" role="alert">Unsaved: {error}</p> : null}
      {status === 'pending' ? <p className="dim" role="status">Waiting to save…</p> : null}
      <div className="prompt-actions">
        {template.editable ? <button className="btn btn-quiet" onClick={() => setShowDefault((value) => !value)}>{showDefault ? 'Back to editor' : 'View bundled default'}</button> : null}
        {template.editable && canWrite ? <>
          <button className="btn" disabled={status === 'saving' || (!template.overridden && !autosave.dirty)} onClick={() => void reset()}>Reset to default</button>
          {status === 'saving' ? <Spinner /> : null}
        </> : template.editable ? <p className="dim">Only administrators can edit prompts.</p> : null}
      </div>
    </div>
  </section>;
}
